import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { getPostLoginRedirect } from "./utils/postLoginRedirect";
import { NetworkProvider } from "./context/NetworkContext";
import { OfflineBanner } from "./components/OfflineBanner";
import { Toaster } from "./components/ui/sonner";
import { Capacitor } from "@capacitor/core";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import CreditsPage from "./pages/CreditsPage";
import JobsPage from "./pages/JobsPage";
import PaymentCallbackPage from "./pages/PaymentCallbackPage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import TemplateSelectionPage from "./pages/TemplateSelectionPage";
import ServiceLandingRoute from "./pages/ServiceLandingRoute";
import DocumentGenerationPage from "./pages/DocumentGenerationPage";
import ProposalGenerateRoute from "./pages/ProposalGenerateRoute";
import ServiceProposalWizardPage from "./pages/ServiceProposalWizardPage";
import NgoGrantProposalWizardPage from "./pages/NgoGrantProposalWizardPage";
import BusinessPlanWizardPage from "./pages/BusinessPlanWizardPage";
import { CoverLetterTemplateRedirect } from "./lib/coverLetterRoutes";
import { ProposalCategoryComingSoonPage } from "./pages/ProposalCategoryPage";
import OnboardingPage from "./pages/OnboardingPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";
import PricingPage from "./pages/PricingPage";
import RefundPolicyPage from "./pages/RefundPolicyPage";
import ContactPage from "./pages/ContactPage";
import TemplatesListingPage from "./pages/TemplatesListingPage";
import TemplateSlugRoute from "./pages/TemplateSlugRoute";
import MyLibraryPage from "./pages/MyLibraryPage";

const ProtectedRoute = ({ children, adminOnly = false, allowOnboardingPending = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) {
    const returnTo = `${location.pathname}${location.search}`;
    const loginRedirect = returnTo.startsWith('/login') ? '' : `?redirect=${encodeURIComponent(returnTo)}`;
    return <Navigate to={`/login${loginRedirect}`} replace />;
  }
  if (adminOnly && user.role !== 'admin' && user.role !== 'super_admin') return <Navigate to="/dashboard" replace />;
  // Force onboarding wizard for first-time users (skipped automatically once
  // the user has any completed document — see /api/auth/me).
  if (
    !allowOnboardingPending &&
    user.role === 'user' &&
    user.onboarding_completed === false
  ) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
};

const PublicRoute = ({ children, honorRedirectParam = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user) {
    const destination = honorRedirectParam
      ? getPostLoginRedirect(location.search)
      : '/dashboard';
    return <Navigate to={destination} replace />;
  }
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/proposal" element={<Navigate to="/services/business-proposal-builder" replace />} />
      <Route path="/proposals" element={<Navigate to="/services/business-proposal-builder" replace />} />
      <Route path="/invoice" element={<Navigate to="/services/invoice-generator" replace />} />
      <Route path="/quotation" element={<Navigate to="/services/quotation-generator" replace />} />
      <Route path="/purchase-order" element={<Navigate to="/services/purchase-order-builder" replace />} />
      <Route path="/resume" element={<Navigate to="/services/resume-builder" replace />} />
      <Route path="/proforma-invoice" element={<Navigate to="/services/proforma-invoice" replace />} />
      <Route path="/delivery-note" element={<Navigate to="/services/delivery-note" replace />} />
      <Route path="/waybill" element={<Navigate to="/services/waybill-generator" replace />} />
      <Route path="/services/pdf_compress" element={<Navigate to="/services/pdf-compression" replace />} />
      <Route path="/services/visa_photo" element={<Navigate to="/services/visa-passport-photo" replace />} />
      <Route path="/services/visa-photo" element={<Navigate to="/services/visa-passport-photo" replace />} />
      <Route path="/login" element={<PublicRoute honorRedirectParam><LoginPage /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><SignupPage /></PublicRoute>} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/refund-policy" element={<RefundPolicyPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/templates" element={<TemplatesListingPage />} />
      <Route path="/templates/:slug" element={<TemplateSlugRoute />} />
      <Route path="/my-library" element={<ProtectedRoute><MyLibraryPage /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute allowOnboardingPending><OnboardingPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/credits" element={<ProtectedRoute><CreditsPage /></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute><JobsPage /></ProtectedRoute>} />
      <Route path="/payment/callback" element={<ProtectedRoute><PaymentCallbackPage /></ProtectedRoute>} />
      <Route path="/documents/business-proposal/business-plan/templates" element={<TemplateSelectionPage fixedDocumentType="business-proposal" fixedCategory="business_plan_funding" />} />
      <Route path="/documents/business-proposal/grant-proposal/templates" element={<TemplateSelectionPage fixedDocumentType="business-proposal" fixedCategory="business_grant_proposal" />} />
      <Route path="/documents/business-proposal/service-proposal/templates" element={<TemplateSelectionPage fixedDocumentType="business-proposal" fixedCategory="service_proposal" />} />
      <Route path="/documents/business-proposal/ngo-grant-proposal/templates" element={<TemplateSelectionPage fixedDocumentType="business-proposal" fixedCategory="ngo_grant_proposal" />} />
      <Route path="/documents/cover-letter/templates" element={<CoverLetterTemplateRedirect />} />
      <Route path="/documents/:documentType/templates" element={<TemplateSelectionPage />} />
      <Route path="/documents/business-proposal/categories" element={<Navigate to="/services/business-proposal-builder" replace />} />
      <Route path="/documents/business-proposal/coming-soon" element={<ProtectedRoute><ProposalCategoryComingSoonPage /></ProtectedRoute>} />
      <Route path="/documents/service-proposal/generate" element={<ProtectedRoute><ServiceProposalWizardPage /></ProtectedRoute>} />
      <Route path="/documents/ngo-grant-proposal/generate" element={<ProtectedRoute><NgoGrantProposalWizardPage /></ProtectedRoute>} />
      <Route path="/documents/business-proposal/generate" element={<ProtectedRoute><ProposalGenerateRoute /></ProtectedRoute>} />
      <Route path="/proposals/business-plan" element={<ProtectedRoute><BusinessPlanWizardPage /></ProtectedRoute>} />
      <Route path="/documents/:documentType/generate" element={<ProtectedRoute><DocumentGenerationPage /></ProtectedRoute>} />
      <Route path="/services/:serviceType" element={<ServiceLandingRoute />} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboardPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const handleBackButton = (ev) => {
        ev.preventDefault();
        if (window.history.length > 1) window.history.back();
      };
      document.addEventListener('backbutton', handleBackButton);
      document.addEventListener('ionBackButton', (ev) => {
        ev.detail?.register(10, () => {
          if (window.history.length > 1) window.history.back();
        });
      });
      return () => document.removeEventListener('backbutton', handleBackButton);
    }
  }, []);

  return (
    <NetworkProvider>
      <BrowserRouter>
        <AuthProvider>
          <OfflineBanner />
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </NetworkProvider>
  );
}

export default App;

